import { DEFAULT_OG_PATH, SITE_DESCRIPTION } from './seoSlug';

export interface PageMetaInput {
    documentTitle?: string | null;
    metaDescription?: string | null;
    canonicalUrl?: string | null;
    robots?: string | null;
    profilePhotoUrl?: string | null;
    imageAlt?: string | null;
    jsonLd?: unknown;
    prevUrl?: string | null;
    nextUrl?: string | null;
    username?: string | null;
}

const DEFAULT_TITLE = 'Discover Music Collaborators | JamWith';

function origin(): string {
    if (typeof window === 'undefined') return 'https://app.jamwith.net';
    return window.location.origin;
}

function defaultImage(): string {
    return `${origin()}${DEFAULT_OG_PATH}`;
}

function setMeta(attribute: 'name' | 'property', key: string, content: string): void {
    const selector = `meta[${attribute}="${key}"]`;
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
}

function setLink(rel: string, href: string | null): void {
    const existing = document.head.querySelector(`link[rel="${rel}"]`);
    if (!href) {
        existing?.remove();
        return;
    }
    const element = existing ?? document.createElement('link');
    element.setAttribute('rel', rel);
    element.setAttribute('href', href);
    if (!existing) document.head.appendChild(element);
}

/** Writes the tags for one profile or directory page. Social tags fall back to the site image. */
export function applyPageMeta(meta: PageMetaInput): void {
    const title = meta.documentTitle || DEFAULT_TITLE;
    const description = meta.metaDescription || SITE_DESCRIPTION;
    const url = meta.canonicalUrl || `${origin()}/`;
    const image = meta.profilePhotoUrl || defaultImage();

    setMeta('name', 'description', description);
    setLink('canonical', meta.canonicalUrl || null);
    setMeta('name', 'robots', meta.robots || 'index, follow');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:type', meta.username ? 'profile' : 'website');
    setMeta('property', 'og:image', image);
    if (meta.imageAlt) setMeta('property', 'og:image:alt', meta.imageAlt);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);
    setLink('prev', meta.prevUrl || null);
    setLink('next', meta.nextUrl || null);

    let script = document.head.querySelector('script[data-jamwith="page"]');
    if (!meta.jsonLd) {
        script?.remove();
        return;
    }
    if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-jamwith', 'page');
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(meta.jsonLd).replace(/</g, '\\u003c');
}

/** Puts the shell back to the homepage description and image, without a page canonical. */
export function restoreDefaultMeta(): void {
    applyPageMeta({});
    setLink('canonical', null);
}
